import { h, vaciar, m2, euros, descargarFichero, num } from '../dom';
import { anadir } from '../dom';
import type { Contexto } from '../contexto';
import type { Estado } from '../estado';
import type { CandidatoCompleto } from '../../db/consultas';
import { urlFotoFachada } from '../../catastro/ovc';
import { nombreUso } from '../../catastro/gml';

/** Panel "Candidatos": los inmuebles marcados, con datos propios (anuncio, precio, notas). */
export class CandidatosPanel {
  readonly el = h('section', { class: 'panel-contenido' });
  onComparar: (() => void) | null = null;
  onInformePdf: (() => void) | null = null;

  constructor(private readonly ctx: Contexto) {
    ctx.almacen.suscribir((e, cambios) => { if ('candidatos' in cambios || 'rcSeleccionado' in cambios) this.render(e); });
    this.render(ctx.almacen.estado);
  }

  private render(e: Estado): void {
    vaciar(this.el);
    const lista = e.candidatos;
    anadir(this.el,
      h('h2', null, '3. Candidatos'),
      h('p', { class: 'ayuda' }, 'Marca parcelas con ☆ en los resultados o en la ficha. Aquí puedes anotar el anuncio, el precio y tus notas; todo se guarda solo en este navegador.'),
      lista.length === 0 ? h('p', { class: 'silencio' }, 'Todavía no hay candidatos.') : null,
      ...lista.map((c) => this.tarjeta(c, e.rcSeleccionado === c.rc)),
      h('div', { class: 'fila-botones' },
        lista.length >= 2 ? h('button', { class: 'boton primario', onClick: () => this.onComparar?.() }, 'Comparar') : null,
        lista.length ? h('button', { class: 'boton secundario', onClick: () => this.onInformePdf?.() }, 'Informe PDF') : null,
        lista.length ? h('button', { class: 'boton secundario', onClick: () => this.exportar() }, 'Exportar JSON') : null,
        h('label', { class: 'boton secundario' }, 'Importar JSON',
          h('input', { type: 'file', accept: 'application/json', style: 'display:none', onChange: (ev: Event) => void this.importar(ev) })),
      ),
    );
  }

  private tarjeta(c: CandidatoCompleto, seleccionado: boolean): HTMLElement {
    const p = c.parcela, ed = c.edificio;
    const guardar = (cambios: Partial<CandidatoCompleto>) => void this.guardar({ ...c, ...cambios });
    return h('div', { class: `tarjeta candidato ${seleccionado ? 'seleccionado' : ''}` },
      h('div', { class: 'candidato-cabecera' },
        h('img', { class: 'miniatura', src: urlFotoFachada(c.rc), alt: '', loading: 'lazy', onError: (ev: Event) => { (ev.target as HTMLElement).style.display = 'none'; } }),
        h('div', null,
          h('div', { class: 'item-titulo' }, h('button', { class: 'enlace rc', onClick: () => void this.ctx.seleccionar(c.rc, true) }, c.rc)),
          h('div', { class: 'item-detalle' }, p ? `parcela ${m2(p.areaM2)}` : 'sin datos catastrales cargados',
            ed ? ` · construida ${m2(ed.supConstruidaM2)} · ${ed.plantas ?? '?'} pl. · ${ed.anio ?? '?'} · ${nombreUso(ed.uso)}` : ''),
          c.inmuebles.length ? h('div', { class: 'item-detalle silencio' }, `${num(c.inmuebles.length)} inmuebles consultados`) : null)),
      h('div', { class: 'campo-num' }, h('label', null, 'Etiqueta'),
        h('input', { type: 'text', value: c.etiqueta ?? '', placeholder: 'p. ej. A, B, C', onChange: (ev: Event) => guardar({ etiqueta: (ev.target as HTMLInputElement).value || null }) })),
      h('div', { class: 'campo-num' }, h('label', null, 'Anuncio (URL)'),
        h('input', { type: 'url', value: c.anuncioUrl ?? '', placeholder: 'https://…', onChange: (ev: Event) => guardar({ anuncioUrl: (ev.target as HTMLInputElement).value || null }) }),
        c.anuncioUrl ? h('a', { href: c.anuncioUrl, target: '_blank', rel: 'noopener', class: 'enlace' }, 'abrir') : null),
      h('div', { class: 'campo-num' }, h('label', null, 'Precio'),
        h('input', { type: 'number', value: c.precio !== null ? String(c.precio) : '', placeholder: '€', onChange: (ev: Event) => { const v = (ev.target as HTMLInputElement).value; guardar({ precio: v === '' ? null : Number(v) }); } }),
        c.precio !== null && ed?.supConstruidaM2 ? h('span', { class: 'silencio' }, ` ${euros(c.precio)} · ${euros(c.precio / ed.supConstruidaM2)}/m²`) : null),
      h('div', { class: 'campo-num' }, h('label', null, 'Notas'),
        h('textarea', { rows: 2, value: c.notas ?? '', onChange: (ev: Event) => guardar({ notas: (ev.target as HTMLTextAreaElement).value || null }) })),
      h('div', { class: 'fila-botones' },
        h('button', { class: 'boton secundario', onClick: () => void this.ctx.seleccionar(c.rc, true) }, 'Ver'),
        h('button', { class: 'boton peligro', onClick: () => void this.quitar(c.rc) }, 'Quitar')),
    );
  }

  private async guardar(c: CandidatoCompleto): Promise<void> {
    await this.ctx.db.guardarCandidato({ rc: c.rc, anuncioUrl: c.anuncioUrl, precio: c.precio, etiqueta: c.etiqueta, notas: c.notas, creado: c.creado });
    await this.ctx.refrescarCandidatos();
  }

  private async quitar(rc: string): Promise<void> {
    await this.ctx.db.borrarCandidato(rc);
    await this.ctx.refrescarCandidatos();
    await this.ctx.refrescarZona();
  }

  private exportar(): void {
    const datos = this.ctx.almacen.estado.candidatos.map((c) => ({ rc: c.rc, anuncioUrl: c.anuncioUrl, precio: c.precio, etiqueta: c.etiqueta, notas: c.notas, creado: c.creado }));
    descargarFichero(`candidatos-cernicalo-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify({ version: 1, candidatos: datos }, null, 2), 'application/json');
  }

  private async importar(ev: Event): Promise<void> {
    const entrada = ev.target as HTMLInputElement;
    const fichero = entrada.files?.[0];
    if (!fichero) return;
    try {
      const datos = JSON.parse(await fichero.text()) as { candidatos?: { rc: string; anuncioUrl?: string | null; precio?: number | null; etiqueta?: string | null; notas?: string | null; creado?: number }[] };
      let n = 0;
      for (const c of datos.candidatos ?? []) {
        if (!/^[A-Z0-9]{14}$/i.test(c.rc)) continue;
        await this.ctx.db.guardarCandidato({ rc: c.rc.toUpperCase(), anuncioUrl: c.anuncioUrl ?? null, precio: c.precio ?? null, etiqueta: c.etiqueta ?? null, notas: c.notas ?? null, creado: c.creado ?? Date.now() });
        n++;
      }
      this.ctx.almacen.avisar(`${n} candidato(s) importados.`);
      await this.ctx.refrescarCandidatos();
      await this.ctx.refrescarZona();
    } catch {
      this.ctx.almacen.avisar('El fichero no es un JSON de candidatos válido.', 'error');
    } finally {
      entrada.value = '';
    }
  }
}
